import React, { useState, useEffect } from 'react';
import { 
  X, CheckCircle, AlertTriangle, Loader2, Ticket, ChevronLeft, Lock, ArrowRight
} from 'lucide-react';
import { db } from '../../services/firebase';
import { doc, runTransaction, getDoc, collection } from 'firebase/firestore';

interface GiveawayData {
  id: string;
  name: string;
  brand: string;
  description: string;
  image: string;
  entryCost: number;
  endsAt: string;
}

interface GiveawayTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  giveaway: GiveawayData | null;
  currentUser: any; 
}

export const GiveawayTerminal: React.FC<GiveawayTerminalProps> = ({
  isOpen, onClose, giveaway, currentUser
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [entryStatus, setEntryStatus] = useState<'idle' | 'success' | 'insufficient' | 'error'>('idle');
  const [hasEntered, setHasEntered] = useState(false);
  
  // Instantly grab the balance from the profile if it exists, otherwise wait for DB
  const initialBalance = currentUser?.credits ?? currentUser?.cred ?? currentUser?.balance ?? null;
  const [userBalance, setUserBalance] = useState<number | null>(initialBalance);

  // Safely grab the ID whether your app uses .uid or .id
  const userId = currentUser?.uid || currentUser?.id;

  // Prevent background scrolling when terminal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 1. CHECK IF ALREADY ENTERED & FETCH FRESH BALANCE
  useEffect(() => {
    if (isOpen && giveaway && userId) {
      const initializeTerminal = async () => {
        setEntryStatus('idle');
        
        try {
            // Fetch current fresh user balance from DB
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const data = userSnap.data();
                // Check for all possible naming conventions
                const freshBalance = data.credits ?? data.cred ?? data.balance ?? 0;
                setUserBalance(freshBalance);
            }

            // Check if already entered
            const entryRef = doc(db, 'giveaways', giveaway.id, 'entries', userId);
            const entrySnap = await getDoc(entryRef);
            if (entrySnap.exists()) {
                setHasEntered(true);
            } else {
                setHasEntered(false);
            }
        } catch (error) {
            console.error("Initialization Error:", error);
        }
      };
      initializeTerminal();
    }
  }, [isOpen, giveaway, userId]);

  // 2. THE SECURE FIREBASE TRANSACTION
  const handleAuthorizeEntry = async () => {
    if (!giveaway || !userId) return;
    
    setIsProcessing(true);
    setEntryStatus('idle');

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'users', userId);
        const userSnap = await transaction.get(userRef);
        
        if (!userSnap.exists()) throw new Error("USER_NOT_FOUND");
        
        const data = userSnap.data();
        
        // Detect exact field name used in database for wallet
        const balanceField = data.credits !== undefined ? 'credits' : (data.cred !== undefined ? 'cred' : 'balance');
        const currentCredits = data[balanceField] || 0;
        
        // RULE CHECK: Does the user have enough credits?
        if (currentCredits < giveaway.entryCost) {
            throw new Error("INSUFFICIENT_FUNDS");
        }

        // RULE CHECK: Check if they are trying to double-enter
        const entryRef = doc(db, 'giveaways', giveaway.id, 'entries', userId);
        const entrySnap = await transaction.get(entryRef);
        if (entrySnap.exists()) {
            throw new Error("ALREADY_ENTERED");
        }

        // EXECUTE: Deduct credits and log entry securely
        transaction.update(userRef, { [balanceField]: currentCredits - giveaway.entryCost });
        transaction.set(entryRef, {
            userId: userId,
            timestamp: new Date().toISOString(),
            cost: giveaway.entryCost
        });
      });

      // Transaction Succeeded
      setEntryStatus('success');
      setHasEntered(true);
      if (userBalance !== null) setUserBalance(userBalance - giveaway.entryCost);

    } catch (error: any) {
      console.error("Entry Error:", error);
      if (error.message === "INSUFFICIENT_FUNDS") {
          setEntryStatus('insufficient');
      } else if (error.message === "ALREADY_ENTERED") {
          setHasEntered(true);
      } else {
          setEntryStatus('error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || !giveaway) return null;

  return (
    <div className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-300">
      
      <div className="w-full h-full lg:w-[1000px] lg:h-[700px] bg-black lg:border-2 border-zinc-800 flex flex-col lg:flex-row overflow-hidden shadow-2xl relative">
        
        {/* 🟢 CLOSE BUTTON: Left on Mobile, Right on Desktop. Rounded on Mobile, Sharp on Desktop */}
        <button 
            onClick={onClose} 
            className="absolute top-6 left-6 lg:left-auto lg:top-8 lg:right-8 z-50 p-4 bg-white text-black hover:bg-zinc-200 transition-colors shadow-xl lg:shadow-2xl rounded-full lg:rounded-none border border-black flex items-center justify-center"
        >
            <X size={24} strokeWidth={2.5}/>
        </button>

        <div className="w-full lg:w-1/2 h-[40vh] lg:h-full bg-zinc-950 relative border-b lg:border-b-0 lg:border-r border-zinc-800 shrink-0">
            <img src={giveaway.image} className="w-full h-full object-cover grayscale opacity-80" alt={giveaway.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
            
            <div className="absolute bottom-8 left-8 pr-8">
                <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-[0.4em] mb-2">Target Asset</p>
                <h2 className="text-3xl lg:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">{giveaway.name}</h2>
            </div>
        </div>

        <div className="flex-1 flex flex-col h-full relative overflow-y-auto bg-black">
            <div className="p-8 lg:p-12 flex-1 flex flex-col">
                
                <div className="border-b border-zinc-800 pb-6 mb-8 mt-4 lg:mt-0">
                    <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-3">
                        <Lock size={16} /> Entry Protocol
                    </h3>
                </div>

                {entryStatus === 'success' ? (
                    <div className="flex-1 flex flex-col items-center justify-center animate-in zoom-in-95 duration-300">
                        <CheckCircle size={80} className="text-white mb-8" strokeWidth={1} />
                        <h2 className="text-5xl font-black text-white uppercase italic tracking-tighter mb-4 text-center">Entry<br/>Confirmed</h2>
                        <div className="border border-white p-4 font-mono text-[10px] text-white uppercase tracking-widest text-center mt-4 rounded-xl lg:rounded-none">
                            Receipt ID: GW-{Math.random().toString(36).substr(2, 9).toUpperCase()}
                            <br/>Cost: -{giveaway.entryCost} Credits
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="space-y-8 flex-1">
                            <div>
                                <p className="text-zinc-400 font-mono text-xs uppercase tracking-widest leading-relaxed mb-6">
                                    {giveaway.description || 'Verified Brand Giveaway. Entry fee required to participate. Winners selected randomly by system protocol.'}
                                </p>
                                <div className="border-l-2 border-zinc-700 pl-4 py-1">
                                    <p className="text-white font-black uppercase text-sm tracking-widest">Provider: {giveaway.brand}</p>
                                    <p className="text-zinc-500 font-mono text-[10px] uppercase mt-1">Closes: {giveaway.endsAt ? new Date(giveaway.endsAt).toLocaleDateString() : 'TBA'}</p>
                                </div>
                            </div>

                            {/* 🟢 INFO CARD: Rounded on Mobile, Sharp on Desktop */}
                            <div className="bg-zinc-950 border-2 border-zinc-800 p-6 space-y-4 rounded-2xl lg:rounded-none">
                                <div className="flex justify-between items-center border-b border-zinc-800 pb-4">
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-[0.3em]">Current Balance</span>
                                    <span className="text-sm font-black text-white">{userBalance !== null ? `${userBalance} CRD` : 'LOADING...'}</span>
                                </div>
                                <div className="flex justify-between items-end pt-2">
                                    <span className="text-[10px] font-black text-white uppercase tracking-[0.3em] flex items-center gap-2">
                                        <Ticket size={14}/> Required Entry Fee
                                    </span>
                                    <span className="text-3xl font-black italic tracking-tighter text-white">
                                        {giveaway.entryCost} <span className="text-sm not-italic font-mono text-zinc-500">CRD</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-8 border-t border-zinc-800 shrink-0">
                            {hasEntered ? (
                                /* 🟢 DISABLED BUTTON: Rounded on Mobile, Sharp on Desktop */
                                <button disabled className="w-full py-6 border-2 border-zinc-800 bg-zinc-950 text-zinc-500 font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 cursor-not-allowed rounded-full lg:rounded-none">
                                    <CheckCircle size={16} /> Access Granted / Entered
                                </button>
                            ) : entryStatus === 'insufficient' ? (
                                <div className="space-y-4 animate-in shake duration-300">
                                    {/* 🟢 ERROR ALERT: Rounded on Mobile, Sharp on Desktop */}
                                    <div className="w-full py-6 border-2 border-red-900 bg-red-950/20 text-red-500 font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 rounded-2xl lg:rounded-none">
                                        <AlertTriangle size={18} /> Insufficient Funds
                                    </div>
                                    <button onClick={() => setEntryStatus('idle')} className="w-full text-center text-[10px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest underline underline-offset-4">
                                        Acknowledge
                                    </button>
                                </div>
                            ) : (
                                /* 🟢 PRIMARY BUTTON: Rounded on Mobile, Sharp on Desktop */
                                <button 
                                    onClick={handleAuthorizeEntry}
                                    disabled={isProcessing || userBalance === null}
                                    className="w-full py-6 border-2 border-white bg-white text-black font-black uppercase tracking-[0.2em] text-xs hover:bg-zinc-200 transition-colors flex items-center justify-center gap-4 shadow-xl lg:shadow-2xl disabled:opacity-50 rounded-full lg:rounded-none"
                                >
                                    {isProcessing ? <Loader2 className="animate-spin" size={20} /> : <>Authorize Entry <ArrowRight size={16}/></>}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>

      </div>
    </div>
  );
};